'use client'

import { useCallback, useState } from 'react'
import { useAuth } from '@clerk/nextjs'
import { useDebouncedCallback } from 'use-debounce'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { MoreHorizontal, Search, ShieldCheck, ShieldMinus, UserMinus, LogOut, XCircle } from 'lucide-react'
import { useGetMembersQuery } from '@/api/organization/members/get-members-query'
import { useUpdateMemberRoleMutation } from '@/api/organization/members/update-member-role-mutation'
import { useRemoveMemberMutation } from '@/api/organization/members/remove-member-mutation'
import { useRevokeInvitationMutation } from '@/api/organization/members/revoke-invitation-mutation'
import { ConfirmationModal } from '@/components/ui/confirmation-modal'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { InviteMemberDialog } from './invite-member-dialog'
import { PendingJoinRequestsSection } from './pending-join-requests-section'
import { SettingsSection } from '@/features/settings-modal/settings-section'
import { useIsSuperUser } from '@/utils/clerk/use-is-super-user'
import { toast } from '@/features/toast/toast'
import { useGetMembersPolicyQuery } from '@/api/organization/members/get-members-policy-query'
import { getWorkspaceLeaveBlockedReason } from '../utils/workspace-leave-policy'
import { getApiErrorMessage } from '@/utils/api/get-api-error-message'

export const MembersSection = () => {
    const { userId, orgRole, actor } = useAuth()
    const isAdmin = orgRole === 'org:admin'
    const isSuperUser = !!useIsSuperUser()
    const isImpersonating = !!actor
    // Treat invite + manage as the same capability so an impersonator who
    // can send an invite can also see/revoke the resulting pending row and
    // the per-row Actions menu, instead of getting stuck mid-task.
    const canManage = isAdmin || isSuperUser || isImpersonating
    const [search, setSearch] = useState('')
    const [debouncedSearch, setDebouncedSearch] = useState('')
    const [inviteDialogOpen, setInviteDialogOpen] = useState(false)
    const [confirmAction, setConfirmAction] = useState<{
        type: 'remove' | 'leave' | 'revoke'
        title: string
        subtitle: string
        content?: React.ReactNode
        confirmText: string
        onConfirm: () => Promise<void>
    } | null>(null)

    const debouncedSetSearch = useDebouncedCallback((value: string) => {
        setDebouncedSearch(value)
    }, 300)

    const { data, isLoading } = useGetMembersQuery({ query: debouncedSearch || undefined })
    const { data: membersPolicy } = useGetMembersPolicyQuery()
    const updateRoleMutation = useUpdateMemberRoleMutation()
    const removeMemberMutation = useRemoveMemberMutation()
    const revokeInvitationMutation = useRevokeInvitationMutation()

    const members = data?.members ?? []
    const invitations = data?.invitations ?? []
    const totalCount = data?.totalCount ?? 0
    const cannotLeaveReason = getWorkspaceLeaveBlockedReason(membersPolicy)

    const handleRoleChange = (memberUserId: string, newRole: 'org:admin' | 'org:member') => {
        updateRoleMutation.mutate(
            { userId: memberUserId, role: newRole },
            {
                onSuccess: () => toast.success('Role updated'),
                onError: (error) => toast.error(getApiErrorMessage(error, 'Failed to update role')),
            }
        )
    }

    const handleRemoveMember = useCallback(
        (memberUserId: string, memberName: string) => {
            const isSelf = memberUserId === userId
            setConfirmAction({
                type: isSelf ? 'leave' : 'remove',
                title: isSelf ? 'Leave workspace?' : `Remove ${memberName}?`,
                subtitle: 'This action cannot be undone.',
                content: isSelf ? (
                    'You will lose access to this workspace and all its data.'
                ) : (
                    <>
                        <span className="font-semibold">{memberName}</span> will be removed from this workspace and lose
                        access immediately.
                    </>
                ),
                confirmText: isSelf ? 'Leave workspace' : 'Remove member',
                onConfirm: () =>
                    new Promise<void>((resolve, reject) => {
                        removeMemberMutation.mutate(
                            { userId: memberUserId },
                            {
                                onSuccess: () => {
                                    if (isSelf) {
                                        window.location.href = '/routing'
                                    } else {
                                        toast.success(`${memberName} removed`)
                                    }
                                    resolve()
                                },
                                onError: (error) => {
                                    toast.error(getApiErrorMessage(error, 'Failed to remove member'))
                                    reject()
                                },
                            }
                        )
                    }),
            })
        },
        [userId, removeMemberMutation]
    )

    const handleRevokeInvitation = useCallback(
        (invitationId: string, email: string) => {
            setConfirmAction({
                type: 'revoke',
                title: 'Revoke invitation?',
                subtitle: 'This action cannot be undone.',
                content: (
                    <>
                        The invitation for <span className="font-semibold">{email}</span> will be revoked and they will
                        no longer be able to join.
                    </>
                ),
                confirmText: 'Revoke invitation',
                onConfirm: () =>
                    new Promise<void>((resolve, reject) => {
                        revokeInvitationMutation.mutate(
                            { invitationId },
                            {
                                onSuccess: () => {
                                    toast.success(`Invitation for ${email} revoked`)
                                    resolve()
                                },
                                onError: (error) => {
                                    toast.error(error?.errors?.[0]?.msg || 'Failed to revoke invitation')
                                    reject()
                                },
                            }
                        )
                    }),
            })
        },
        [revokeInvitationMutation]
    )

    const formatDate = (timestamp: number | null) => {
        if (!timestamp) return '—'
        return new Date(timestamp).toLocaleDateString('en-US', {
            month: 'numeric',
            day: 'numeric',
            year: 'numeric',
        })
    }

    const getInitials = (firstName: string | null, lastName: string | null, email: string | null) => {
        if (firstName || lastName) {
            return `${firstName?.charAt(0) ?? ''}${lastName?.charAt(0) ?? ''}`.toUpperCase()
        }
        return email?.charAt(0)?.toUpperCase() ?? '?'
    }

    const formatRole = (role: string) => {
        return role === 'org:admin' ? 'Admin' : 'Member'
    }

    return (
        <>
            <SettingsSection
                title={`Members${totalCount ? ` (${totalCount})` : ''}`}
                cta={
                    canManage ? (
                        <Button variant="ds2-primary" size="ds2" onClick={() => setInviteDialogOpen(true)}>
                            Invite
                        </Button>
                    ) : undefined
                }
            >
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                        placeholder="Search members..."
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value)
                            debouncedSetSearch(e.target.value)
                        }}
                        className="pl-9"
                    />
                </div>

                {isLoading ? (
                    <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
                        Loading members...
                    </div>
                ) : members.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-8 text-sm text-muted-foreground">
                        No members found for &quot;{debouncedSearch}&quot;
                    </div>
                ) : (
                    <div className="rounded-lg border overflow-hidden">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>User</TableHead>
                                    <TableHead>Joined</TableHead>
                                    <TableHead>Role</TableHead>
                                    {canManage && <TableHead className="w-[50px]">Actions</TableHead>}
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {members.map((member) => {
                                    const name = [member.firstName, member.lastName].filter(Boolean).join(' ')
                                    const displayName = name || member.email || 'Unknown'
                                    const isSelf = member.userId === userId

                                    return (
                                        <TableRow key={member.userId} className="hover:bg-transparent">
                                            <TableCell>
                                                <div className="flex items-center gap-3">
                                                    <Avatar className="h-8 w-8">
                                                        <AvatarImage
                                                            src={member.imageUrl ?? undefined}
                                                            alt={displayName}
                                                        />
                                                        <AvatarFallback className="text-xs">
                                                            {getInitials(
                                                                member.firstName,
                                                                member.lastName,
                                                                member.email
                                                            )}
                                                        </AvatarFallback>
                                                    </Avatar>
                                                    <div className="flex flex-col">
                                                        <span className="text-sm font-medium">
                                                            {displayName}
                                                            {isSelf && (
                                                                <span className="text-muted-foreground font-normal ml-1.5">
                                                                    You
                                                                </span>
                                                            )}
                                                        </span>
                                                        {name && member.email && (
                                                            <span className="text-xs text-muted-foreground">
                                                                {member.email}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-sm text-muted-foreground">
                                                {formatDate(member.joinedAt)}
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant="outline">{formatRole(member.role)}</Badge>
                                            </TableCell>
                                            {canManage && (
                                                <TableCell>
                                                    <DropdownMenu>
                                                        <DropdownMenuTrigger asChild>
                                                            <Button variant="ghost" size="icon" className="h-8 w-8">
                                                                <MoreHorizontal className="h-4 w-4" />
                                                            </Button>
                                                        </DropdownMenuTrigger>
                                                        <DropdownMenuContent
                                                            align="end"
                                                            className="min-w-[160px] rounded-xl p-1 shadow-lg border-stone-100"
                                                        >
                                                            {!isSelf && (
                                                                <DropdownMenuItem
                                                                    onClick={() =>
                                                                        handleRoleChange(
                                                                            member.userId,
                                                                            member.role === 'org:admin'
                                                                                ? 'org:member'
                                                                                : 'org:admin'
                                                                        )
                                                                    }
                                                                    className="cursor-pointer rounded-lg text-stone-600 data-highlighted:bg-none data-highlighted:bg-stone-50 data-highlighted:text-stone-800"
                                                                >
                                                                    {member.role === 'org:admin' ? (
                                                                        <ShieldMinus className="h-3.5 w-3.5 mr-2" />
                                                                    ) : (
                                                                        <ShieldCheck className="h-3.5 w-3.5 mr-2" />
                                                                    )}
                                                                    {member.role === 'org:admin'
                                                                        ? 'Change to Member'
                                                                        : 'Change to Admin'}
                                                                </DropdownMenuItem>
                                                            )}
                                                            <Tooltip>
                                                                <TooltipTrigger asChild>
                                                                    <DropdownMenuItem
                                                                        className="cursor-pointer rounded-lg text-red-600 data-highlighted:bg-none data-highlighted:bg-red-50 data-highlighted:text-red-700 aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
                                                                        onSelect={(event) => {
                                                                            if (isSelf && cannotLeaveReason) {
                                                                                event.preventDefault()
                                                                                return
                                                                            }
                                                                            handleRemoveMember(
                                                                                member.userId,
                                                                                displayName
                                                                            )
                                                                        }}
                                                                        aria-disabled={isSelf && !!cannotLeaveReason}
                                                                    >
                                                                        {isSelf ? (
                                                                            <LogOut className="h-3.5 w-3.5 mr-2" />
                                                                        ) : (
                                                                            <UserMinus className="h-3.5 w-3.5 mr-2" />
                                                                        )}
                                                                        {isSelf ? 'Leave workspace' : 'Remove member'}
                                                                    </DropdownMenuItem>
                                                                </TooltipTrigger>
                                                                {isSelf && cannotLeaveReason && (
                                                                    <TooltipContent className="max-w-[260px] text-left">
                                                                        {cannotLeaveReason}
                                                                    </TooltipContent>
                                                                )}
                                                            </Tooltip>
                                                        </DropdownMenuContent>
                                                    </DropdownMenu>
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    )
                                })}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </SettingsSection>

            {canManage && <PendingJoinRequestsSection />}

            {canManage && invitations.length > 0 && (
                <SettingsSection title="Pending invitations">
                    <div className="rounded-lg border overflow-hidden">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Email</TableHead>
                                    <TableHead>Invited</TableHead>
                                    <TableHead>Role</TableHead>
                                    <TableHead className="w-[50px]">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {invitations.map((invitation) => (
                                    <TableRow key={invitation.id} className="hover:bg-transparent">
                                        <TableCell className="text-sm">{invitation.emailAddress}</TableCell>
                                        <TableCell className="text-sm text-muted-foreground">
                                            {formatDate(invitation.createdAt)}
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant="outline">{formatRole(invitation.role)}</Badge>
                                        </TableCell>
                                        <TableCell>
                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                    <Button variant="ghost" size="icon" className="h-8 w-8">
                                                        <MoreHorizontal className="h-4 w-4" />
                                                    </Button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent
                                                    align="end"
                                                    className="min-w-[160px] rounded-xl p-1 shadow-lg border-stone-100"
                                                >
                                                    <DropdownMenuItem
                                                        className="cursor-pointer rounded-lg text-red-600 data-highlighted:bg-none data-highlighted:bg-red-50 data-highlighted:text-red-700"
                                                        onClick={() =>
                                                            handleRevokeInvitation(
                                                                invitation.id,
                                                                invitation.emailAddress
                                                            )
                                                        }
                                                    >
                                                        <XCircle className="h-3.5 w-3.5 mr-2" />
                                                        Revoke invitation
                                                    </DropdownMenuItem>
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                </SettingsSection>
            )}

            <InviteMemberDialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen} />

            <ConfirmationModal
                open={!!confirmAction}
                onOpenChange={(open) => {
                    if (!open) setConfirmAction(null)
                }}
                title={confirmAction?.title ?? ''}
                subtitle={confirmAction?.subtitle}
                content={confirmAction?.content}
                confirmText={confirmAction?.confirmText ?? 'Confirm'}
                onConfirm={confirmAction?.onConfirm ?? (() => {})}
                destructive
            />
        </>
    )
}
